import { Box, Stack, Typography } from '@mui/material'
import { useMemo } from 'react'
import { TAttribute } from '../../../../../types'
import * as sxStyles from '../AnalyticsDetails.styled'
import { AnalysisAttributes, CardTitle } from '../../../../../components'
import { Container } from '../../../../Shipment/pages/IncomingDeliveriesDetails/components/ConsistencyResult/ConsistencyResult.styled'
import InfoIcon from '@mui/icons-material/Info'

type FlourAnalysisAttributesProps = {
  attributes: TAttribute[]
  genericAttributes: any[]
}

function shouldDisplayConsistencyInfo(attributes: TAttribute[]): boolean {
  let hasHiddenInconsistent = false
  let hasVisibleInconsistent = false

  for (const attr of attributes) {
    const name = attr.consistency?.name
    if (name !== 'Consistent') {
      if (attr.show_attribute) {
        hasVisibleInconsistent = true
      } else {
        hasHiddenInconsistent = true
      }
    }
    if (hasHiddenInconsistent && hasVisibleInconsistent) {
      break
    }
  }

  return hasHiddenInconsistent && !hasVisibleInconsistent
}

type GroupedCategory = {
  id: number
  name: string
  attributes: TAttribute[]
}

export const AnalysisAttributesSection = ({
  attributes,
  genericAttributes,
}: FlourAnalysisAttributesProps) => {
  const displayOutOfRangeTooltip = shouldDisplayConsistencyInfo(attributes)

  const flattenGenericAttributes = useMemo(
    () =>
      genericAttributes.flatMap((category) =>
        category.attributes.map((attr: any) => ({
          ...attr,
          category_name: category.name,
        }))
      ),
    [genericAttributes]
  )

  const groupedSorted: GroupedCategory[] = useMemo(() => {
    const groupedMap = new Map<number, GroupedCategory & { order: number }>()

    for (const attr of attributes) {
      if (!attr.show_attribute) continue

      const id = attr.attribute_category_id
      const name = attr.attribute_category
      const order = attr.attribute_category_order

      if (!groupedMap.has(id)) {
        groupedMap.set(id, {
          id,
          name,
          order,
          attributes: [],
        })
      }

      groupedMap.get(id)!.attributes.push(attr)
    }

    return Array.from(groupedMap.values()).sort((a, b) => a.order - b.order)
  }, [attributes])

  return (
    <>
      <Stack>
        <Typography variant="h5">NIR Parameters</Typography>
        {displayOutOfRangeTooltip && (
          <Stack flexDirection="row" alignItems="center" gap={1}>
            <InfoIcon fontSize="small" color="error" />
            <Typography variant="caption" color="error">
              There are hidden out of range parameters, which affect consistency
              result. Use filter button to display them
            </Typography>
          </Stack>
        )}
      </Stack>
      <Stack sx={sxStyles.attributesSectionContainer}>
        {groupedSorted.map(({ id, name, attributes }) => (
          <Stack flex={1} key={id}>
            <Container>
              <Box px="16px">
                <CardTitle title={name} />
              </Box>
              <AnalysisAttributes
                attributes={attributes}
                defaultWheatCodeAttributes={flattenGenericAttributes}
              />
            </Container>
          </Stack>
        ))}
      </Stack>
    </>
  )
}
